import qs from "qs"
import { Fragment, useEffect, useState, useCallback } from "react"
import {
  LoaderCircleIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  EyeIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
} from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import { Outlet, useLoaderData, useFetcher, useRouteLoaderData, useSearchParams } from "react-router"
import { useForm } from "react-hook-form"
import { getOrganisations } from "@/utils/projects"
import { fetchAPI, handleResponse, reactHookFormSubmitShim, throwIfNotOk } from "@/utils"
import { FiltersWrapper, type StoredSession, type OnBeforeSubmit } from "@/components/Filters"
import type { RootLoader } from "../root"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { hasPermission } from "@/machines/Authmachine"
import Pagination from "@/components/ui/pagination"
import z from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  formatFluentValidationErrors,
  formatRHFErrors,
  type FluentValidationError,
  type ValidationSummaryData,
} from "@/utils/forms"
import { ValidationSummary } from "@/components/ui/ValidationSummary"

const routeId = "root-causes-routeId"
const sessionKey = "root-causes-filter"

interface RootCause {
  organisation: string
  name: string
  code: string
  value: string
  active: boolean
}

const createValidationSchema = <T extends "CREATE" | "UPDATE">(action: T) => {
  return z.object({
    action: z.literal(action),
    organisation: z.string().min(1),
    name: z
      .string()
      .trim()
      .min(1, "Name is required")
      .max(100, "Name should be a maximum of 100 characters")
      .regex(/^[a-zA-Z0-9 ]+$/, "Only alpha numeric and space characters are allowed"),
    code: z
      .string()
      .trim()
      .min(1, "Code is required")
      .max(50, "Code should be a maximum of 50 characters")
      .regex(/^[a-zA-Z0-9 ]+$/, "Only alpha numeric and space characters are allowed"),
    value: z.string().trim().min(1),
  })
}

const RootCauseCreateSchema = createValidationSchema("CREATE")
const RootCauseEditSchema = createValidationSchema("UPDATE")

const RootCauseDeleteSchema = z.object({
  action: z.literal("DELETE"),
  organisation: z.string().trim().min(1),
  name: z.string().trim().min(1),
})

const RootCauseToggleActiveSchema = z.object({
  action: z.literal("TOGGLEACTIVE"),
  organisation: z.string().trim().min(1),
  name: z.string().min(1).trim(),
  active: z.preprocess((val) => {
    if (typeof val === "string") {
      if (val.toLowerCase() === "true") return true
      if (val.toLowerCase() === "false") return false
    }
    return val
  }, z.boolean()),
})

const RootCauseEditOrCreateSchema = z.discriminatedUnion("action", [RootCauseCreateSchema, RootCauseEditSchema])

const RootCauseSchema = z.discriminatedUnion("action", [
  RootCauseCreateSchema,
  RootCauseEditSchema,
  RootCauseToggleActiveSchema,
  RootCauseDeleteSchema,
])

interface RootCauseAPIResponse {
  items: RootCause[]
  page: number
  totalCount: number
}

interface ActionReturn {
  action: "CREATE" | "UPDATE" | "DELETE" | "TOGGLEACTIVE"
  errorMessage?: string
  errors: Record<string, string[]>
}

async function loader({ request }: { request: Request }) {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const url = new URL(request.url)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]
  const includeInactive = filters.parsed?.includeInactive ?? false

  try {
    const response = await fetchAPI<RootCauseAPIResponse>(
      `/api/unityReview/RootCauses/${loadedOrganisation}/${page}/${pageSize}?includeInactive=${includeInactive}`,
    ).then(handleResponse)

    return {
      rootCauses: response.items,
      loadedOrganisation,
      totalCount: response.totalCount,
      page: response.page,
    }
  } catch (error) {
    if (error != null && typeof error === "object" && "status" in error && error.status === 404) {
      return { rootCauses: [], loadedOrganisation, totalCount: 0, page: 1 }
    }
    throw error
  }
}

async function mainAction({ request }: { request: Request }): Promise<ActionReturn> {
  const rawBody = qs.parse(await request.text())
  const { data, success, error } = RootCauseSchema.safeParse(rawBody)

  let errorMessage: string | undefined
  let errors: Record<string, string[]> = {}

  if (!success) {
    console.log(error)
    return {
      action: "CREATE",
      errorMessage: "Failed to parse the form contents",
      errors: {},
    }
  }

  switch (data.action) {
    case "CREATE":
    case "UPDATE": {
      const { action, organisation, ...payload } = data
      try {
        await fetchAPI(`/api/unityReview/RootCauses/${organisation}`, {
          method: "POST",
          body: JSON.stringify(payload),
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response?.status === 400) {
          errors = formatFluentValidationErrors((await response.json()) as FluentValidationError[])
        } else {
          console.error(error)
          errorMessage = action === "CREATE" ? "Error creating root cause" : "Error updating root cause"
        }
      }
      break
    }
    case "DELETE": {
      try {
        await fetchAPI(`/api/unityReview/RootCauses/${data.organisation}/${data.name}`, { method: "DELETE" }).then(
          throwIfNotOk,
        )
      } catch (error) {
        const response = error as Response
        if (response?.status === 400) {
          errors = formatFluentValidationErrors((await response.json()) as FluentValidationError[])
        } else {
          console.error(error)
          errorMessage = "Error deleting root cause"
        }
      }
      break
    }
    case "TOGGLEACTIVE": {
      try {
        await fetchAPI(`/api/unityReview/RootCauses/${data.organisation}/${data.name}`, {
          method: "PUT",
          body: JSON.stringify({ active: !data.active }),
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response?.status === 400) {
          errors = formatFluentValidationErrors((await response.json()) as FluentValidationError[])
        } else {
          console.error(error)
          errorMessage = "Error toggling root cause active status"
        }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action: data.action, errorMessage, errors }
}

function RootCausePreview({ rootCause }: { rootCause: RootCause }) {
  return (
    <Dialog>
      <Tooltip>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <EyeIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <TooltipContent>Preview Root Cause</TooltipContent>
      </Tooltip>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{rootCause.name}</DialogTitle>
          <DialogDescription>Code: {rootCause.code}</DialogDescription>
        </DialogHeader>
        <textarea value={rootCause.value} readOnly rows={8} className="w-full rounded-md border bg-gray-50 p-2" />
        <div className="flex justify-end">
          <DialogClose asChild>
            <Button variant="ghost">Close</Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ToggleActive({
  rootCause,
  onError,
  onSuccess,
}: {
  rootCause: RootCause
  onError: (errors: ValidationSummaryData) => void
  onSuccess: () => void
}) {
  const { register, handleSubmit } = useForm<
    z.input<typeof RootCauseToggleActiveSchema>,
    any,
    z.output<typeof RootCauseToggleActiveSchema>
  >({
    resolver: zodResolver(RootCauseToggleActiveSchema),
    values: {
      action: "TOGGLEACTIVE" as const,
      organisation: rootCause.organisation,
      name: rootCause.name,
      active: rootCause.active,
    },
  })
  const fetcher = useFetcher<ActionReturn>({ key: "rc_silent_forms" })

  const isSubmitting = fetcher.state !== "idle"

  const onSubmit = handleSubmit(
    (formData) => {
      // This will ONLY run if Zod is 100% happy
      // Clear any zod errors down.
      onSuccess()
      reactHookFormSubmitShim(formData, fetcher.submit)
    },
    (errors) => {
      onError(formatRHFErrors(errors))
    },
  )

  return (
    <fetcher.Form method="POST" onSubmit={(e) => void onSubmit(e)}>
      <input type="hidden" {...register("action")} />
      <input type="hidden" {...register("organisation")} />
      <input type="hidden" {...register("name")} />
      <input type="hidden" {...register("active")} />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            disabled={isSubmitting}
            size="icon"
            variant="ghost"
            type="submit"
            className="h-8 w-8 cursor-pointer opacity-50"
          >
            {rootCause.active ? (
              <ToggleRightIcon className="h-4 w-4 text-green-600" />
            ) : (
              <ToggleLeftIcon className="h-4 w-4 text-gray-600" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{rootCause.active ? "Deactivate" : "Activate"}</TooltipContent>
      </Tooltip>
    </fetcher.Form>
  )
}

function RootCauseDeleteForm({
  rootCause,
  onError,
  onSuccess,
}: {
  rootCause: RootCause
  onError: (errors: ValidationSummaryData) => void
  onSuccess: () => void
}) {
  const { register, handleSubmit } = useForm<z.infer<typeof RootCauseDeleteSchema>>({
    resolver: zodResolver(RootCauseDeleteSchema),
    values: {
      action: "DELETE" as const,
      organisation: rootCause.organisation,
      name: rootCause.name,
    },
  })
  const fetcher = useFetcher<ActionReturn>({ key: "rc_silent_forms" })

  const onSubmit = handleSubmit(
    (formData) => {
      onSuccess()
      reactHookFormSubmitShim(formData, fetcher.submit)
    },
    (errors) => {
      onError(formatRHFErrors(errors))
    },
  )

  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-red-600 hover:text-red-700 disabled:cursor-not-allowed"
            >
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Delete Root Cause</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="start" className="max-w-sm">
        <p className="text-sm text-pretty">
          Root Cause &quot;<span className="font-semibold">{rootCause.name}</span>&quot; will be deleted. Are you sure?
        </p>
        <fetcher.Form
          method="DELETE"
          className="flex items-center justify-start gap-3 pt-4"
          onSubmit={(e) => void onSubmit(e)}
        >
          <input type="hidden" {...register("action")} />
          <input type="hidden" {...register("organisation")} />
          <input type="hidden" {...register("name")} />
          {fetcher.state !== "idle" && fetcher.formData?.get("name") === rootCause.name && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
            </span>
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button variant="ghost">Cancel</Button>
          </PopoverClose>
          <Button type="submit" variant="destructive">
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()
  const url = new URL(window.location.href)
  const page = +(url.searchParams.get("page") ?? 1)
  const pageSize = +(url.searchParams.get("pageSize") ?? 10)

  const pagingAdjustment = (page - 1) * pageSize
  const [globalErrors, setGlobalErrors] = useState<Record<string, string[]>>({})
  const fetcher = useFetcher<ActionReturn>({ key: "rc_silent_forms" })

  const serverErrors = fetcher.data?.errors
  const combinedErrors = { ...globalErrors, ...serverErrors }

  return (
    <div className="h-screen overflow-y-auto">
      <Pagination routeId={routeId} />
      <ValidationSummary errors={combinedErrors} />
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-12" />
            <TableHead className="w-24 text-left lg:w-32 xl:w-64">Name</TableHead>
            <TableHead className="w-24 text-left lg:w-32 xl:w-64">Code</TableHead>
            <TableHead className="text-left">Value</TableHead>
            <TableHead className="w-24 text-left">Active</TableHead>
            <TableHead className="w-48">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rootCauses.length > 0 ? (
            data.rootCauses.map((item, index) => (
              <TableRow key={`${item.organisation}-${item.name}`} className="text-sm">
                <TableCell>{index + 1 + pagingAdjustment}.</TableCell>
                <TableCell className="break-words whitespace-normal">{item.name}</TableCell>
                <TableCell className="break-words whitespace-normal">{item.code}</TableCell>
                <TableCell className="overflow-hidden text-ellipsis whitespace-nowrap">{item.value}</TableCell>
                <TableCell>
                  <Badge variant={item.active ? "default" : "secondary"}>{item.active ? "Active" : "Inactive"}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <RootCausePreview rootCause={item} />
                    <RootCauseEditOrCreate rootCause={item} />
                    <ToggleActive rootCause={item} onError={setGlobalErrors} onSuccess={() => setGlobalErrors({})} />
                    <RootCauseDeleteForm
                      rootCause={item}
                      onError={setGlobalErrors}
                      onSuccess={() => setGlobalErrors({})}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="py-4 text-center text-gray-500">
                No root causes found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

export function RootCauses() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [, setSearchParams] = useSearchParams()

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((formData, reason) => {
    if (reason.type !== "field") return

    // Reset the page number based on our new filters
    if (reason.name === "organisation" || reason.name === "includeInactive") {
      formData.delete("page")
      setSearchParams((urlSearchParams) => {
        urlSearchParams.delete("page")
        return urlSearchParams
      })
    }
  }, [])

  return (
    <Fragment>
      <div className="flex items-center gap-4">
        {rootData?.organisations != null && rootData.organisations.length > 0 && (
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data?.loadedOrganisation ?? rootData.organisations[0]}
              items={rootData.organisations}
            />
            <FiltersWrapper.Checkbox name="includeInactive" label="Include Inactive" />
          </FiltersWrapper>
        )}
        <div className="flex-1" />
        <RootCauseEditOrCreate />
      </div>
      <Outlet />
    </Fragment>
  )
}

function RootCauseEditOrCreate({ rootCause }: { rootCause?: RootCause }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const [open, setOpen] = useState(false)

  const isEditing = rootCause != null

  const closeDialog = useCallback(() => {
    setOpen(false)
  }, [])

  if (data == null || hasPermission("writeAccess") !== true) return null

  // Helper to render the actual button
  const TriggerButton = isEditing ? (
    <Button variant="ghost" size="icon" className="h-8 w-8">
      <PencilIcon className="h-4 w-4" />
      <span className="sr-only">Edit Root Cause</span>
    </Button>
  ) : (
    <Button variant="outline">
      <PlusIcon className="mr-2 h-4 w-4" /> Add New
    </Button>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {isEditing ? (
        /* Outer: Tooltip -> Middle: TooltipTrigger -> Inner: DialogTrigger */
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>Edit Root Cause</TooltipContent>
        </Tooltip>
      ) : (
        <DialogTrigger asChild>{TriggerButton}</DialogTrigger>
      )}

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add New"} Root Cause</DialogTitle>
          <DialogDescription>
            {isEditing ? (
              <>
                Editing <span className="font-semibold">{rootCause.name}</span>
              </>
            ) : (
              "Create a new route cause"
            )}
          </DialogDescription>
        </DialogHeader>

        <RootCauseEditOrCreateForm rootCause={rootCause} closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function RootCauseEditOrCreateForm({ rootCause, closeDialog }: { rootCause?: RootCause; closeDialog: () => void }) {
  const data = useRouteLoaderData<typeof loader>(routeId)
  const fetcher = useFetcher<typeof mainAction>()

  const isEditing = rootCause != null

  const rootData = useRouteLoaderData<RootLoader>("root")

  const getCurrentOrganisation = () => {
    if (data?.loadedOrganisation != null && data.loadedOrganisation !== "") return data.loadedOrganisation

    const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
    return filters.parsed?.organisation ?? rootData?.organisations[0]
  }

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<z.infer<typeof RootCauseEditOrCreateSchema>>({
    resolver: zodResolver(RootCauseEditOrCreateSchema),
    values: {
      action: isEditing ? "UPDATE" : "CREATE",
      organisation: rootCause?.organisation ?? getCurrentOrganisation() ?? "",
      name: rootCause?.name ?? "",
      code: rootCause?.code ?? "",
      value: rootCause?.value ?? "",
    },
  })

  // Combine both sources of errors into one "Summary" shape
  const zodSummary = formatRHFErrors(errors)
  const serverSummary = fetcher.data?.errors // Already in Record<string, string[]> format

  // Merge them (server errors usually take priority if both exist)
  const combinedErrors = { ...zodSummary, ...serverSummary }

  useEffect(() => {
    const result = fetcher.data
    if (result == null) return
    const hasNoValidationErrors = result.errors == null || Object.keys(result.errors).length === 0
    const hasNoGlobalError = result.errorMessage == null

    if (hasNoGlobalError && hasNoValidationErrors) {
      closeDialog()
      if (!isEditing) reset()
    }
  }, [closeDialog, isEditing, reset, fetcher.data])

  if (data == null || !hasPermission("writeAccess")) return null

  const onSubmit = handleSubmit(async (formData) => {
    if (!isEditing) {
      try {
        const response = await fetchAPI<RootCause>(
          `/api/UnityReview/RootCauses/${data?.loadedOrganisation}/${formData.name}`,
        ).then(handleResponse)

        if (response != null && Object.keys(response).length > 0) {
          setError("name", { message: "That root cause name already exists, please update the name to make it unique" })
          return
        }
      } catch (error) {
        const response = error as Response
        if (response == null || (response.status !== 404 && response.status !== 204)) {
          console.error(error)
          setError("name", { message: "There was a problem identifying if this is a duplicate entry or not" })
          return
        }
      }
    }

    clearErrors()
    fetcher.submit(formData, { method: "POST" })
  })

  return (
    <fetcher.Form method="POST" className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
      <input type="hidden" {...register("action")} />
      <input type="hidden" {...register("organisation")} />

      <ValidationSummary errors={combinedErrors} />
      {isEditing ? (
        <div>
          <label className="mb-2 block text-sm font-medium">Name</label>
          <Input {...register("name")} disabled className="bg-gray-50" />
          <p className="mt-1 text-xs text-gray-500">Name cannot be changed after creation</p>
        </div>
      ) : (
        <div>
          <label className="mb-2 block text-sm font-medium">Name</label>
          <Input {...register("name")} aria-required="true" placeholder="Enter unique name" />
        </div>
      )}

      <div>
        <label className="mb-2 block text-sm font-medium">Code</label>
        <Input {...register("code")} aria-required="true" placeholder="Enter code" />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium">Value</label>
        <textarea
          {...register("value")}
          aria-required="true"
          placeholder="Enter value"
          rows={5}
          className="w-full rounded-md border p-2"
        />
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> {isEditing ? "Updating" : "Creating"}
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit">{isEditing ? "Update" : "Create"}</Button>
      </div>
    </fetcher.Form>
  )
}

RootCauses.Main = Main
RootCauses.loader = loader
RootCauses.routeId = routeId

Main.action = mainAction
